import { action, computed } from 'mobx'
import { func, type IValidationSchema, validationContext } from 'validx'
import type { ReplaceMutator } from '@/features/legends/effects/effects'
import {
  MutatorEditorViewModelBase,
  type MutatorEditorViewModelOptions,
} from '@/features/legends/effects/MutatorEditorViewModelBase'
import type { EntityDescriptor } from '@/features/legends/editor/entities'
import type { RynoType } from '@/features/legends/editor/ryno-types'
import { RynoEditorViewModel } from '@/features/legends/ryno/RynoEditorViewModel'
import type { SymbolConnectorViewModel } from '@/features/legends/symbols/SymbolConnectorViewModel'

/**
 * Puts a new value into variables.
 */
export class ReplaceMutatorEditorViewModel extends MutatorEditorViewModelBase<ReplaceMutator> {
  private readonly _schema: IValidationSchema<ReplaceMutatorEditorViewModel> = {
    ...this.targetsSchema,
    expr: [func(() => this.expr.analyze(), 'There are formula errors.')],
  }

  readonly type = 'Replace'

  /**
   * The new value.
   */
  readonly expr: RynoEditorViewModel

  readonly validation = validationContext(this, this._schema)

  constructor(options: MutatorEditorViewModelOptions) {
    super(options, 'Please select the variable(s) you wish to change.')
    this.expr = new RynoEditorViewModel({
      required: true,
      getSymbolTable: () => this.symbolTable,
      getNeededType: () => this.targets.valueType,
    })
    this.lifecycle.manage(this.expr)
  }

  /**
   * Formulas can use `Value`, the variable's current value.
   */
  @computed
  get symbolTable() {
    return this._symbolTableWithValue(this.targets.valueType)
  }

  /** @inheritDoc */
  @action
  hydrate(mutator: ReplaceMutator) {
    super.hydrate(mutator)
    this.expr.setValue(mutator.expr)
  }

  /** @inheritDoc */
  toJSON(): ReplaceMutator {
    return {
      type: this.type,
      targets: this.targets.toJSON(),
      expr: this.expr.toJSON(),
    }
  }

  /**
   * Any variable, and once there is more than one target, only variables of
   * the first target's type.
   */
  protected _isCompatibleTarget(
    { type }: EntityDescriptor,
    valueType: RynoType | null,
    connector: SymbolConnectorViewModel,
  ) {
    if (type !== 'Variable') {
      return false
    }
    return this._matchesFirstTarget(valueType, connector)
  }
}
